import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Stack, Text, Image, HStack, Heading } from '@chakra-ui/react';
import { SelectMenu } from '@spotoninc/nexus-react';
import { FinancialInsightSubHeader } from './financialInsightSubHeader';
import LocationAnalysisTable from './locationAnalysisTable';
import { useSelector } from 'react-redux';
import { fetchWithAuth } from '../../utils/apiHelpers';
import {
  extractIntegrationIds,
  extractLocationIds,
} from '../../utils/integrationUtils';
import OvertimeImage from '../../media/images/OvertimeImage.png';
import './reskinned-time-range-selector.css';

const getDurationRangeOptions = (
  locationData,
  selectedLocations,
  periodSwitcher
) => {
  let baseKeyValues = [
    {
      label: 'Last 10 Days',
      value: 'last10Days',
      key: 'cost_analysis_group_by_condition_byday',
      interval: '1 day',
    },
    {
      label: 'Last 10 Weeks',
      value: 'last10Weeks',
      key: 'cost_analysis_group_by_condition_byweek',
      interval: '1 week',
    },
    {
      label: 'Last 15 Months',
      value: 'last15Months',
      key: 'cost_analysis_group_by_condition_bymonth',
      interval: '1 month',
    },
    {
      label: 'Last 6 Quarters',
      value: 'last6Quarters',
      key: 'cost_analysis_group_by_condition_byquarter',
      interval: '3 months',
    },
    {
      label: 'Last 4 Years',
      value: 'last4Years',
      key: 'cost_analysis_group_by_condition_byyear',
      interval: '1 year',
    },
  ];

  const selectedValues = Array.isArray(selectedLocations)
    ? selectedLocations
    : selectedLocations
      ? [selectedLocations]
      : [];

  console.log('[PROF-447 DEBUG] selectedValues:', selectedValues);
  console.log('[PROF-447 DEBUG] locationData count:', locationData.length);
  console.log(
    '[PROF-447 DEBUG] locationData sample:',
    locationData.slice(0, 3)
  );

  if (selectedValues.length === 0) return baseKeyValues;

  // if all selected locations have show_pcal=true then periods will be shown else
  // it wont display periods tab
  const shouldShowPeriods = selectedValues.every(selectedLocation => {
    console.log('[PROF-447 DEBUG] Checking location:', selectedLocation);

    // Handle integration-level selection (e.g., "INTEGRATION::285")
    // For integrations, use the periodSwitcher Redux state which is set by /api/fetch_periods
    if (String(selectedLocation).startsWith('INTEGRATION::')) {
      console.log(
        '[PROF-447 DEBUG] Integration detected, periodSwitcher:',
        periodSwitcher
      );
      return periodSwitcher === true;
    }

    // Handle individual location selection
    const locationInfo = locationData.find(
      loc => loc.value === selectedLocation
    );

    if (!locationInfo) {
      console.log('[PROF-447 DEBUG] Location not found:', selectedLocation);
      return false;
    }

    try {
      const parts = String(locationInfo.ddl_value).split('|');
      const showPcalPart = parts.find(p =>
        p.trim().toLowerCase().startsWith('show_pcal=')
      );
      const val = showPcalPart?.split('=')[1]?.trim().toLowerCase();
      console.log(
        '[PROF-447 DEBUG] Individual location',
        selectedLocation,
        'show_pcal:',
        val
      );
      return val === 'true';
    } catch (error) {
      console.error('[PROF-447 DEBUG] Error parsing:', error);
      return false;
    }
  });

  console.log('[PROF-447 DEBUG] Final shouldShowPeriods:', shouldShowPeriods);
  // add periods option when all selected locations support period calendars
  if (shouldShowPeriods) {
    const insertIndex =
      baseKeyValues.findIndex(option => option.value === 'last15Months') + 1;
    baseKeyValues.splice(insertIndex, 0, {
      label: 'Last 15 Periods',
      value: 'last15Periods',
      key: 'cost_analysis_group_by_condition_byperiod',
      interval: '1 month',
    });
  }
  return baseKeyValues;
};

const parseValue = value => {
  if (value === null) return 0;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    // Remove $ and any other non-numeric characters except decimal point and minus sign
    return parseFloat(value.replace(/[^0-9.-]+/g, '')) || 0;
  }
  return 0;
};

const calculateDateRange = selectedDate => {
  const today = new Date();
  const toDate = today
    .toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    })
    .replace(/\//g, '-');

  let fromDate = new Date(today);

  switch (selectedDate?.value) {
    case 'last10Days':
      fromDate.setDate(today.getDate() - 10);
      break;
    case 'last10Weeks':
      fromDate.setDate(today.getDate() - 70); // 10 weeks * 7 days
      break;
    case 'last15Months':
      fromDate.setMonth(today.getMonth() - 15);
      break;
    case 'last15Periods':
      fromDate.setMonth(today.getMonth() - 15);
      break;
    case 'last6Quarters':
      fromDate.setMonth(today.getMonth() - 18); // 6 quarters * 3 months
      break;
    case 'last4Years':
      fromDate.setFullYear(today.getFullYear() - 4);
      break;
    default:
      fromDate.setMonth(today.getMonth() - 15); // Default to 15 months
  }

  fromDate = fromDate
    .toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    })
    .replace(/\//g, '-');

  return { fromDate, toDate };
};

const ComparisonOvertime = ({
  setLocation,
  locationValue,
  periodFrom,
  periodTo,
  periodSelect,
  dateValue,
  value,
}) => {
  const locationData = useSelector(
    state => state.locationSelectFormat.locationData
  );
  const periodSwitcher = useSelector(state => state.dateFormat.periodSwitcher);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState('cost_amt');
  const [nameType, setNameType] = useState('Overview');
  const [interval, setInterval] = useState('15 months');
  const [selectedDate, setSelectedDate] = useState({
    label: 'Last 15 Months',
    value: 'last15Months',
    key: 'cost_analysis_group_by_condition_bymonth',
    interval: '1 month',
  });
  // Initialize date range with default values
  const defaultDateRange = calculateDateRange({
    value: 'last15Months',
  });
  const [fromDate, setFromDate] = useState(defaultDateRange.fromDate);
  const [toDate, setToDate] = useState(defaultDateRange.toDate);

  const availableDateKeys = useMemo(() => {
    return getDurationRangeOptions(locationData, locationValue, periodSwitcher);
  }, [locationData, locationValue, periodSwitcher]);

  // Ensure current selection remains valid when options change (e.g., switching integrations/locations)
  useEffect(() => {
    const isValid = availableDateKeys?.some(
      opt => opt.value === selectedDate?.value
    );
    if (!isValid) {
      const fallback =
        availableDateKeys?.find(opt => opt.value === 'last15Months') ||
        availableDateKeys?.[0];
      if (fallback) {
        setSelectedDate(fallback);
        setInterval(fallback.value);
        const { fromDate: calculatedFromDate, toDate: calculatedToDate } =
          calculateDateRange(fallback);
        setFromDate(calculatedFromDate);
        setToDate(calculatedToDate);
      }
    }
  }, [availableDateKeys, selectedDate]);

  const fetchData = useCallback(async () => {
    if (locationValue?.length !== 0) {
      setLoading(true);

      // Extract integration IDs from selected locations (full integrations only)
      const integrationIds = extractIntegrationIds(locationValue, locationData);

      // Extract location IDs from selected locations (individual locations only)
      const locationIds = extractLocationIds(locationValue, locationData);

      // Only error if we have neither integration IDs nor location IDs
      if (integrationIds.length === 0 && locationIds.length === 0) {
        const selectedLocationValues = Array.isArray(locationValue)
          ? locationValue
          : [locationValue];
        console.error(
          'No valid integration IDs or location IDs found for selected locations:',
          selectedLocationValues,
          '| Total selected:',
          selectedLocationValues.length
        );
        setLoading(false);
        return;
      }

      const body = new FormData();
      // Only send integration_ids if user selected integrations
      if (integrationIds.length > 0) {
        body.append('integration_ids', integrationIds.join(','));
      }
      // Only send location_ids if user selected locations
      if (locationIds.length > 0) {
        body.append('location_ids', locationIds.join(','));
      }
      body.append('fromDate', fromDate);
      body.append('toDate', toDate);
      body.append(
        'date_key',
        (selectedDate && selectedDate.key) ||
          'cost_analysis_group_by_condition_bymonth'
      );
      body.append('type', type);
      body.append(
        'interval',
        (selectedDate && selectedDate.interval) || '1 month'
      );

      try {
        const response = await fetchWithAuth(
          '/api/ddl_value_generator_multiselect_date/',
          {
            method: 'POST',
            body: body,
          }
        );

        const result = await response.json();

        if (result.code === undefined) {
          setData(result);
        } else {
          window.open('/', '_self');
          alert('Session Expired!.');
        }
      } catch (err) {
        console.error('Error fetching comparison overtime data:', err);
      } finally {
        setLoading(false);
      }
    }
  }, [locationValue, fromDate, toDate, selectedDate, type, locationData]);

  useEffect(() => {
    // Only fetch data if we have both fromDate and toDate
    if (fromDate && toDate && locationValue?.length !== 0) {
      fetchData();
    }
  }, [
    locationValue,
    type,
    fromDate,
    toDate,
    periodFrom,
    periodTo,
    selectedDate,
    interval,
    fetchData,
  ]);

  const handleDownload = () => {
    if (!data || !data.length) return;

    // Get all unique date columns
    const dateColumns = data.reduce((columns, item) => {
      Object.keys(item).forEach(key => {
        if (
          key !== 'desc' &&
          key !== 'classification' &&
          !columns.includes(key)
        ) {
          columns.push(key);
        }
      });
      return columns;
    }, []);

    // Prepare headers
    const headers = ['Description', 'Classification', ...dateColumns];

    // Helper function to escape CSV values
    const escapeCSV = value => {
      // If the value contains commas, quotes, or newlines, wrap it in quotes
      // Also escape any existing quotes by doubling them
      if (
        value &&
        (value.includes(',') || value.includes('"') || value.includes('\n'))
      ) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    // Prepare data rows
    const rows = data.map(item => {
      const row = [
        escapeCSV(item.desc || ''),
        escapeCSV(item.classification || ''),
        ...dateColumns.map(column => {
          const value = item[column];
          // Format the value with dollar sign and commas, then escape for CSV
          return value
            ? escapeCSV(`$${parseValue(value).toLocaleString()}`)
            : '';
        }),
      ];
      return row;
    });

    // Create CSV content
    const csvContent = [
      headers.map(escapeCSV).join(','),
      ...rows.map(row => row.join(',')),
    ].join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `comparison_overtime_${type}_${new Date().toISOString().split('T')[0]}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleViewTypeChange = (_, newType) => {
    setType(newType);
    switch (newType) {
      case 'cost_amt':
        setNameType('Overview');
        break;
      case 'cost_per':
        setNameType('% of cost');
        break;
      case 'sales_per':
        setNameType('% of sales');
        break;
      default:
        setNameType('Overview');
    }
  };

  // Get unique date columns from the data
  const columns =
    data.length > 0
      ? data.reduce((columns, item) => {
          Object.keys(item).forEach(key => {
            if (
              key !== 'desc' &&
              key !== 'classification' &&
              !columns.includes(key)
            ) {
              columns.push(key);
            }
          });
          return columns;
        }, [])
      : [];

  return (
    <Box>
      <Box mb={4}>
        <HStack
          width="100%"
          justify={{ base: 'flex-start', md: 'space-between' }}
          align={{ base: 'flex-start', md: 'center' }}
          spacing={6}
          paddingBottom={4}
          flexDirection={{ base: 'column', md: 'row' }}
          gap={{ base: 4, md: 6 }}
        >
          <Heading size="lg" className="reskinned-comparison-overtime-header">
            Comparison Over Time
          </Heading>
          <SelectMenu
            placeholder="Select Interval"
            initialValue={selectedDate?.value ?? 'last15Months'}
            options={availableDateKeys}
            onChange={(_, value) => {
              const selectedDateObj = availableDateKeys.find(
                d => d.value === value
              );
              const finalSelection =
                selectedDateObj ||
                availableDateKeys.find(d => d.value === 'last15Months') ||
                availableDateKeys?.[0];
              if (!finalSelection) return;
              setInterval(finalSelection.value);
              setSelectedDate(finalSelection);
              const { fromDate: calculatedFromDate, toDate: calculatedToDate } =
                calculateDateRange(finalSelection);
              setFromDate(calculatedFromDate);
              setToDate(calculatedToDate);
            }}
            className="reskinned-time-range-selector"
            style={{ width: '200px' }}
            data-testid="interval-selector"
          />
        </HStack>
      </Box>

      <Stack spacing={4}>
        <FinancialInsightSubHeader
          locationValue={locationValue}
          setLocation={setLocation}
          setViewType={handleViewTypeChange}
          viewType={nameType}
          handleDownload={handleDownload}
        />
        {locationValue?.length > 0 ? (
          <LocationAnalysisTable
            data={data}
            columns={columns}
            loading={loading}
            type={type}
            selectedDate={selectedDate}
          />
        ) : (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minH="calc(100vh - 500px)"
            w="100%"
            p={4}
          >
            <Image
              src={OvertimeImage}
              alt="Comparison Overtime - Select a location Image"
              mb={16}
            />

            <Text fontSize={18} fontWeight={600}>
              Select a location
            </Text>
            <Text
              fontSize={16}
              fontWeight={400}
              maxW={320}
              textAlign={'center'}
            >
              Select one or more locations to generate a detailed analysis.
            </Text>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default ComparisonOvertime;
